import { useRef, useState, type ReactNode } from "react";
import { useNavigate } from "react-router";
import {
  AlertTriangle,
  Archive,
  ArchiveRestore,
  CalendarPlus,
  ChevronRight,
  ClipboardList,
  ExternalLink,
  Loader2,
  MessageSquarePlus,
  PencilLine,
} from "lucide-react";
import { KitButton } from "@/app/routes/dashboard/components/kit/Button";
import DateInput from "@/components/form/DateInput";
import { channelWarnings } from "@/app/routes/dashboard/content-calendar/channelWarnings";
import { SourceChip } from "@/app/routes/dashboard/content-calendar/components/PitchBoard";
import ProductionBriefDoc from "@/app/routes/dashboard/content-calendar/components/ProductionBriefDoc";
import ProductionBriefEditor from "@/app/routes/dashboard/content-calendar/components/ProductionBriefEditor";
import ReferenceWell from "@/app/routes/dashboard/content-calendar/components/ReferenceWell";
import {
  formatDuration,
  MONTH_NAMES,
} from "@/app/routes/dashboard/content-calendar/calendar.utils";
import { getAiRequestErrorMessage } from "@/lib/api";
import { queue } from "@/components/Toast";
import { useCalendarViewStore } from "@/stores/calendarViewStore";
import { emptyBodyFor, type ProductionBriefBody } from "@/types/productionBrief.type";
import type { WorkshopIdea, WorkshopIdeaBrief } from "@/types/workshop.type";
import {
  useWorkshopIdea,
  useWorkshopIdeaMutations,
} from "../api/useWorkshopIdeas";
import { expandIdeaToBriefPrompt } from "../ideaPrompts";
import { useViewerId } from "../attribution";
import { usePermissions } from "@/hooks/usePermissions";
import { savedByOf } from "./docAttribution";
import { StateTag } from "./WorkshopParts";
import { useOutputActions } from "./useOutputActions";

/** The YYYY-MM-DD a date input wants, N days from now. UTC, like every slot. */
function utcDayInput(offsetDays: number): string {
  const day = new Date();
  day.setUTCDate(day.getUTCDate() + offsetDays);
  return day.toISOString().slice(0, 10);
}

function IdeaChannelWarnings(input: Parameters<typeof channelWarnings>[0]) {
  const warnings = channelWarnings(input);
  if (warnings.length === 0) return null;
  return (
    <ul className="flex flex-col gap-0.5">
      {warnings.map((w) => (
        <li
          key={w.platform + w.level}
          className={`flex items-start gap-1 text-[10.5px] leading-snug ${
            w.level === "HARD" ? "text-red-500" : w.level === "SOFT" ? "text-co-attention" : "text-co-faint"
          }`}
        >
          <AlertTriangle className="mt-0.5 size-3 shrink-0" />
          {w.message}
        </li>
      ))}
    </ul>
  );
}

/** "Idea · video · 9:16 · 45 s", the line under the title in the pen. */
function ideaMetaLine(idea: WorkshopIdea): string {
  const kind = idea.mediaType.toLowerCase().replaceAll("_", " ");
  const length =
    idea.durationSeconds != null && idea.durationSeconds < 60
      ? `${idea.durationSeconds} s`
      : formatDuration(idea.durationSeconds);
  return ["Idea", kind, idea.aspectRatio, length].filter(Boolean).join(" · ");
}

type IdeaTab = "concept" | "brief";

/**
 * The idea's index: the concept, then its brief. Hooks and the claim sit in
 * the concept; they are not their own places.
 */
function IdeaJumps({
  active,
  briefVersion,
  onJump,
}: {
  active: IdeaTab;
  /** The brief's live version, or null when none is written. */
  briefVersion: number | null;
  onJump: (jump: IdeaTab) => void;
}) {
  const jumps: { id: IdeaTab; label: string }[] = [
    { id: "concept", label: "Concept" },
    { id: "brief", label: briefVersion === null ? "Brief" : `Brief v${briefVersion}` },
  ];
  return (
    <nav className="ws-jumps" aria-label="Idea sections">
      {jumps.map((jump) => (
        <button
          key={jump.id}
          type="button"
          className="ws-jump"
          aria-current={active === jump.id ? "true" : undefined}
          onClick={() => onJump(jump.id)}
        >
          {jump.label}
        </button>
      ))}
    </nav>
  );
}

/**
 * The idea's production brief: the calendar's own document and editor, so a
 * brief reads and edits the same before and after it reaches the calendar.
 */
function IdeaBriefSection({
  brief,
  brandId,
  editable,
  editing,
  draftSeed,
  onEdit,
  onCancel,
  onSave,
}: {
  brief: WorkshopIdeaBrief;
  brandId: string;
  editable: boolean;
  editing: boolean;
  /** A body whose save failed, reopened so the typing isn't lost. */
  draftSeed: ProductionBriefBody | null;
  onEdit: () => void;
  onCancel: () => void;
  onSave: (body: ProductionBriefBody, changeNote: string | undefined) => Promise<unknown>;
}) {
  const live = brief.revisions[0] ?? null;
  const body = live?.body ?? null;

  return (
    <section className="ws-idea-section" aria-label="Production brief">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1.5">
          <h3 className="ws-idea-brief-label">
            {brief.kind === "STATIC" ? "Static design brief" : "Video production brief"}
            <StateTag>V{brief.version}</StateTag>
          </h3>
          <p className="tt-read-s text-co-faint">
            Version {brief.version}
            {live ? `, ${live.authorType === "AI" ? "written by the chat" : `edited by ${live.authorName}`}` : ""}
            {live?.changeNote ? `: ${live.changeNote}` : ""}
          </p>
        </div>
        {editable && !editing ? (
          <KitButton
            variant="secondary"
            iconLeft={PencilLine}
            className="tt-read-m h-9 px-3"
            onClick={onEdit}
          >
            Edit brief
          </KitButton>
        ) : null}
      </div>

      {editing && editable ? (
        <ProductionBriefEditor
          brief={brief}
          brandId={brandId}
          body={draftSeed ?? body ?? emptyBodyFor(brief.kind)}
          onSave={onSave}
          onCancel={onCancel}
          saving={false}
        />
      ) : body ? (
        <ProductionBriefDoc kind={brief.kind} body={body} showHeader={false} />
      ) : (
        <p className="tt-read-s text-co-faint">Nothing written yet.</p>
      )}
    </section>
  );
}

type Props = {
  projectId: string;
  brandId: string;
  ideaId: string | null;
  canWrite: boolean;
  /** Back to the room's Outputs (the breadcrumb, and after it goes to the calendar). */
  onClose: () => void;
  /** The pane's own controls (Full width, Close), at the end of the head. */
  actions?: ReactNode;
  /** Paste a message into the project chat for the user to send. */
  onDraftInChat?: (message: string) => void;
  /** Open the chat this idea came from. */
  onOpenThread?: (threadId: string) => void;
};

/**
 * An idea beside the chat: the pane head (Outputs › Idea, Archive or Restore),
 * then the idea as the pen draws it: one title, the meta line, the concept
 * (synopsis, hooks to test, why it earns a place, sources), and its brief.
 */
export default function IdeaDetailModal({
  projectId,
  brandId,
  ideaId,
  canWrite,
  onClose,
  actions,
  onDraftInChat,
  onOpenThread,
}: Props) {
  const navigate = useNavigate();
  const ideaQuery = useWorkshopIdea(ideaId);
  const idea = ideaQuery.data ?? null;
  const { migrateIdea, editBrief } = useWorkshopIdeaMutations(projectId, brandId);
  const outputActions = useOutputActions(projectId, brandId);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [jump, setJump] = useState<IdeaTab>("concept");
  // Adding to the calendar writes the team's calendar, which a Guest only reviews.
  const { isGuest } = usePermissions();
  const viewerId = useViewerId();

  const [date, setDate] = useState(() => utcDayInput(1));
  const [tab, setTab] = useState<IdeaTab>("concept");
  const [editingBrief, setEditingBrief] = useState(false);
  const [briefDraftSeed, setBriefDraftSeed] = useState<ProductionBriefBody | null>(null);

  const [boundId, setBoundId] = useState(ideaId);
  if (ideaId !== boundId) {
    setBoundId(ideaId);
    setDate(utcDayInput(1));
    setTab("concept");
    setJump("concept");
    setEditingBrief(false);
    setBriefDraftSeed(null);
  }

  const archived = idea?.status === "ARCHIVED";
  const migrated = idea?.status === "MIGRATED";
  const hasBrief = !!idea?.brief;
  const briefEditable = canWrite && idea?.status === "ACTIVE";
  const canExpandIntoBrief = !hasBrief && briefEditable && !!onDraftInChat;

  const submitMigrate = () => {
    if (!idea || !date) return;
    migrateIdea.mutate(
      { ideaId: idea.id, date },
      {
        onSuccess: (result) => {
          queue.add({
            title: result.withBrief
              ? `Added to ${MONTH_NAMES[result.month - 1]}, brief ready to send`
              : `Added to ${MONTH_NAMES[result.month - 1]}, signed off`,
            description: "Opening the post on the calendar.",
            variant: "success",
          });
          onClose();
          // Move the calendar's own memory before handing it the URL. It keeps
          // the last-viewed month in persisted state, and leaving that pointing
          // at some other month means the two disagree on arrival — the month
          // the user is being sent to has to win, and it is genuinely the month
          // they are now looking at.
          useCalendarViewStore.getState().setView({
            year: result.year,
            month: result.month,
          });
          useCalendarViewStore.getState().setLayoutMode("list");
          // Straight to the post itself, not just the month: the calendar reads
          // year/month/slot off the URL and opens that slot's workroom.
          navigate(
            `/dashboard/content-calendar?year=${result.year}&month=${result.month}&slot=${result.slotId}`,
          );
        },
        onError: (error) =>
          queue.add({
            title: "Couldn't add this to the calendar",
            description: getAiRequestErrorMessage(error),
            variant: "error",
          }),
      },
    );
  };

  // Archive happens at once with the five second Undo; Restore at once.
  const toggleArchive = () => {
    if (!idea) return;
    const target = { kind: "idea" as const, id: idea.id, title: idea.title };
    if (archived) outputActions.restore(target);
    else outputActions.archive(target);
  };

  const goTo = (next: IdeaTab) => {
    setJump(next);
    setTab(next);
    window.requestAnimationFrame(() => {
      scrollRef.current?.scrollTo({ top: 0 });
    });
  };

  // Optimistic: the editor closes on Save and the new version shows at once. A
  // refused save rolls the brief back and reopens the editor on what was typed.
  const saveBrief = (body: ProductionBriefBody, changeNote: string | undefined) => {
    if (!idea?.brief) return Promise.resolve();
    setEditingBrief(false);
    setBriefDraftSeed(null);
    editBrief.mutate(
      { ideaId: idea.id, baseVersion: idea.brief.version, body, changeNote },
      {
        onError: (error) => {
          setBriefDraftSeed(body);
          setEditingBrief(true);
          setTab("brief");
          queue.add({
            title: "Couldn't save the brief",
            description: getAiRequestErrorMessage(error),
            variant: "error",
          });
        },
      },
    );
    return Promise.resolve();
  };

  if (!ideaId) return null;

  const from = idea ? savedByOf(idea.attribution?.createdBy, viewerId) : null;
  const briefLive = idea?.brief?.revisions[0] ?? null;

  return (
    <section className="ws-idea-lens ws-pane-doc" aria-label={idea?.title ?? "Concept idea"}>
      <header className="ws-pane-head">
        <nav className="ws-pane-crumb" aria-label="Where this is">
          <button type="button" onClick={onClose} className="ws-pane-crumb-back" title="Back to Outputs">
            Outputs
          </button>
          <ChevronRight className="size-3 shrink-0" strokeWidth={1.8} aria-hidden="true" />
          <span className="ws-pane-crumb-here" aria-current="page">
            Idea
          </span>
        </nav>
        <div className="ws-pane-actions">
          {canWrite && idea ? (
            <button type="button" className="ws-outline-btn" onClick={toggleArchive}>
              {archived ? (
                <ArchiveRestore className="size-3.5" strokeWidth={1.8} aria-hidden="true" />
              ) : (
                <Archive className="size-3.5" strokeWidth={1.8} aria-hidden="true" />
              )}
              {archived ? "Restore" : "Archive"}
            </button>
          ) : null}
          {actions}
        </div>
      </header>
      {ideaQuery.isLoading ? (
        <div className="ws-pane-doc-top" aria-busy="true" aria-label="Loading the idea">
          <span className="ws-skel ws-skel--tag" />
          <span className="ws-skel ws-skel--title" />
          <span className="ws-skel ws-skel--line" style={{ width: "86%", marginTop: 20 }} />
          <span className="ws-skel ws-skel--line" style={{ width: "70%" }} />
        </div>
      ) : !idea ? (
        <div className="ws-pane-doc-top">
          <p className="ws-pane-doc-saved">
            {ideaQuery.isError ? null : (
              <Loader2 className="me-1.5 inline size-3.5 animate-spin" aria-hidden />
            )}
            {ideaQuery.isError ? getAiRequestErrorMessage(ideaQuery.error) : "Loading the idea"}
          </p>
          {ideaQuery.isError ? (
            <KitButton variant="secondary" className="mt-3 w-fit" onClick={() => void ideaQuery.refetch()}>
              Try again
            </KitButton>
          ) : null}
        </div>
      ) : (
        <div className="ws-idea-detail">
          <div ref={scrollRef} className="ws-idea-scroll custom-scrollbar">
            <div className="ws-pane-doc-top">
              <h2 className="ws-pane-doc-title">{idea.title}</h2>
              <p className="ws-idea-meta">
                {ideaMetaLine(idea)}
                {from?.thread ? (
                  <>
                    {", from "}
                    {onOpenThread ? (
                      <button
                        type="button"
                        className="ws-doc-thread"
                        onClick={() => onOpenThread(from.thread!.id)}
                      >
                        {from.thread.title}
                      </button>
                    ) : (
                      from.thread.title
                    )}
                  </>
                ) : null}
              </p>
              <IdeaJumps
                active={jump}
                briefVersion={idea.brief?.version ?? null}
                onJump={goTo}
              />
            </div>

            {/* Both panels stay mounted and hide, so switching mid-edit never
                throws away a half-typed brief. */}
            <div id="ws-idea-panel-concept" role="region" aria-label="Concept" hidden={tab !== "concept"}>
              <div className="ws-idea-column">
                <section className="ws-idea-section">
                  <h3 className="sr-only">The concept</h3>
                  <p className="ws-idea-synopsis">{idea.synopsis}</p>
                </section>

                {idea.hooks.length > 0 ? (
                  <section className="ws-idea-section">
                    <h3>Hooks to test</h3>
                    <ol className="ws-idea-hooklist">
                      {idea.hooks.map((hook, index) => (
                        <li key={`${hook}-${index}`}>
                          <span className="ws-idea-hook-label">Hook {index + 1}</span>
                          <span>{hook}</span>
                        </li>
                      ))}
                    </ol>
                  </section>
                ) : null}

                {idea.objectiveClaim ? (
                  <section className="ws-idea-section">
                    <h3>Why it earns a place</h3>
                    <p className="ws-idea-synopsis">{idea.objectiveClaim}</p>
                  </section>
                ) : null}

                {idea.reference ? (
                  <ReferenceWell reference={idea.reference} className="ws-idea-reference" />
                ) : null}

                {idea.channels.length > 0 ? (
                  <IdeaChannelWarnings
                    mediaType={idea.mediaType}
                    aspectRatio={idea.aspectRatio}
                    videoLength={idea.videoLength}
                    channels={idea.channels}
                  />
                ) : null}

                {idea.sources.length > 0 ? (
                  <section className="ws-idea-section">
                    <h3>Sources</h3>
                    <div className="ws-idea-sources">
                      {idea.sources.map((source, index) => (
                        <SourceChip key={`${idea.id}-source-${index}`} source={source} rounded />
                      ))}
                    </div>
                  </section>
                ) : null}

                {idea.brief ? (
                  <button type="button" className="ws-idea-brief-link" onClick={() => goTo("brief")}>
                    <span className="ws-idea-brief-link-title">
                      {idea.brief.kind === "STATIC" ? "Static design brief" : "Video production brief"}
                    </span>
                    <StateTag>V{idea.brief.version}</StateTag>
                    {briefLive ? (
                      <span className="ws-idea-brief-link-by">
                        {briefLive.authorType === "AI" ? "written by the chat" : `edited by ${briefLive.authorName}`}
                      </span>
                    ) : null}
                    <ChevronRight className="ms-auto size-4 shrink-0" strokeWidth={1.8} aria-hidden="true" />
                  </button>
                ) : null}
              </div>
            </div>

            <div id="ws-idea-panel-brief" role="region" aria-label="Brief" hidden={tab !== "brief"}>
              {idea.brief ? (
                <IdeaBriefSection
                  brief={idea.brief}
                  brandId={brandId}
                  editable={briefEditable}
                  editing={editingBrief}
                  draftSeed={briefDraftSeed}
                  onEdit={() => setEditingBrief(true)}
                  onCancel={() => {
                    setEditingBrief(false);
                    setBriefDraftSeed(null);
                  }}
                  onSave={saveBrief}
                />
              ) : (
                <div className="ws-idea-nobrief">
                  <ClipboardList className="size-4 text-co-faint" aria-hidden />
                  <p className="tt-read-m font-medium text-co-text2">No creative brief yet</p>
                  <p className="tt-read-s max-w-[46ch] text-co-muted">
                    {migrated
                      ? "This idea went onto the calendar as a concept. Its brief is written there."
                      : canExpandIntoBrief
                        ? "Use Expand into brief to have the chat write one from this concept."
                        : "No brief has been written for this concept."}
                  </p>
                </div>
              )}
            </div>
          </div>

          {migrated || (canWrite && !archived) ? (
            <div className="ws-idea-footer">
              {migrated ? (
                <div className="ws-idea-calendar-actions">
                  <StateTag>On calendar</StateTag>
                  <KitButton
                    variant="secondary"
                    iconLeft={ExternalLink}
                    className="tt-read-m h-9 px-3"
                    onClick={() => navigate("/dashboard/content-calendar")}
                  >
                    Open calendar
                  </KitButton>
                </div>
              ) : (
                <div className="ws-idea-calendar-actions">
                  {isGuest ? null : (
                    <DateInput
                      label="Goes out on"
                      variant="field"
                      value={date}
                      minValue={utcDayInput(0)}
                      isDisabled={migrateIdea.isPending}
                      onChange={setDate}
                    />
                  )}
                  {canExpandIntoBrief ? (
                    <KitButton
                      variant="secondary"
                      iconLeft={MessageSquarePlus}
                      className="tt-read-m h-9 px-3"
                      onClick={() => onDraftInChat?.(expandIdeaToBriefPrompt(idea))}
                    >
                      Expand into brief
                    </KitButton>
                  ) : null}
                  {isGuest ? null : (
                    <KitButton
                      iconLeft={CalendarPlus}
                      loading={migrateIdea.isPending}
                      isDisabled={!date || editingBrief}
                      className="tt-read-m h-9 px-3.5"
                      onClick={submitMigrate}
                    >
                      Add to calendar
                    </KitButton>
                  )}
                </div>
              )}
            </div>
          ) : null}
        </div>
      )}
    </section>
  );
}
